import { Button, Icon, Toggle, Typography } from '@insurely/ui';
import classNames from 'classnames';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useNavigate } from 'react-router-dom';

import { OVERVIEW_PATH, OVERVIEW_WEALTH_HOME } from '@main/constants';
import { useSharedData, useUseCase, useUserInput } from '@main/contexts';
import { useCompanies } from '@main/hooks';
import { Account, AccountState } from '@main/types';

import { PlateContainer } from '../../../WealthOverview/components/PlateContainer';

import { hideOrDeleteAccount } from './hideOrDeleteAccount';
import styles from './settings.module.css';

interface SettingsProps {
  account: Account;
}

export const Settings = ({ account }: SettingsProps) => {
  const [isHidden, setIsHidden] = useState(account.state === AccountState.HIDDEN);
  const { formatMessage } = useIntl();
  const { nextBlock } = useUseCase();
  const [
    {
      userConfig: { dataAggregation, authToken },
    },
  ] = useSharedData();
  const { setSelectedCompanies } = useUserInput();
  const { data: allCompanies } = useCompanies();
  const navigate = useNavigate();

  const currentSelectedCompany = allCompanies?.find(
    (item) => item.insuranceCompany === account.company,
  );

  const updateAccount = () => {
    if (dataAggregation?.multiSelect && !!currentSelectedCompany) {
      setSelectedCompanies([currentSelectedCompany]);
    }
    nextBlock();
  };

  const settingAccount = async ({
    state,
    onError,
    onSuccess,
  }: {
    state: AccountState;
    onError?: () => void;
    onSuccess?: () => void;
  }) => {
    await hideOrDeleteAccount({
      authToken,
      accountId: account.id,
      state,
      onError,
      onSuccess,
    });
  };

  const toggleHideAccount = async () => {
    setIsHidden(!isHidden);
    await settingAccount({
      state: isHidden ? AccountState.DISPLAYED : AccountState.HIDDEN,
      onError: () => setTimeout(() => setIsHidden(account.state === AccountState.HIDDEN), 300),
    });
  };

  const navigateToOverview = () => navigate(`${OVERVIEW_PATH}/${OVERVIEW_WEALTH_HOME}`);

  const deleteAccount = () =>
    settingAccount({ state: AccountState.REMOVED, onSuccess: navigateToOverview });

  return (
    <PlateContainer className={classNames(styles.plate)}>
      <Typography component="h3" variant="ParagraphCaption">
        {formatMessage({
          id: 'overview.wealth-account.settings-headline-1',
          defaultMessage: 'Settings for overview',
        })}
      </Typography>
      <Toggle
        checked={isHidden}
        id="overview-wealth-settings-toggle"
        onCheckedChange={toggleHideAccount}
        className={styles.toggle}
        label={
          <div className={styles.toggleTextContainer}>
            <Typography
              component="label"
              variant="ParagraphBodySmall"
              htmlFor="overview-wealth-settings-toggle"
            >
              {formatMessage({
                id: 'overview.wealth-account.settings-toggle-headline',
                defaultMessage: 'Hide account',
              })}
            </Typography>
            <Typography component="p" variant="ParagraphTiny">
              {formatMessage({
                id: 'overview.wealth-account.settings-toggle-paragraph',
                defaultMessage:
                  'The value of the account is not added to the total amounts and is placed in the hidden part of the account list.',
              })}
            </Typography>
          </div>
        }
      />
      <Typography component="h3" variant="ParagraphCaption" className={styles.updateHeadline}>
        {formatMessage({
          id: 'overview.wealth-account.settings-headline-2',
          defaultMessage: 'Handle data',
        })}
      </Typography>
      <Button
        variant="ghost"
        icon={<Icon className={styles.icon} name="reload" size={24} />}
        iconPosition="left"
        className={classNames(styles.updateButton, styles.overwrite)}
        onClick={updateAccount}
      >
        <Typography component="span" variant="Label-2" className={styles.updateButtonText}>
          {formatMessage({
            id: 'overview.wealth-account.settings-update-button',
            defaultMessage: 'Update data',
          })}
        </Typography>
      </Button>
      <Button
        variant="ghost"
        icon={<Icon className={styles.icon} name="delete" size={24} />}
        iconPosition="left"
        className={classNames(styles.deleteButton, styles.overwrite)}
        onClick={deleteAccount}
      >
        <Typography component="span" variant="Label-2" className={styles.deleteButtonText}>
          {formatMessage({
            id: 'overview.wealth-account.settings-delete-button',
            defaultMessage: 'Remove account from overview',
          })}
        </Typography>
      </Button>
    </PlateContainer>
  );
};
